import { all, call, put, takeEvery } from 'redux-saga/effects';
import { parseQuery } from '@jjj/components';
import { IResponse } from '@dash/apiInstance';
import Api from '../../../helpers/api';
import { DOCUMENT_MAX_SIZE } from '../../../helpers/fileValidation';
import { sendRequest } from '../../../helpers/saga';
import { logger } from '../../../helpers/logger';
import { t } from '../../../i18n';
import notifications from '../../../redux/notifications/actions';
import actions from './actions';
import { LoadDataAction, UploadFileAction, DownloadTemplateAction } from './actions.d';
import actionTypes from './actionTypes';
import { ILoadDataSuccessPayload, IBulkUploadsFormDataApiResponse, IBulkUploadsTemplateApiResponse } from './types';

const bulkUploadsApi = new Api('bulk_uploads');
const bulkUploadsTemplateApi = new Api('bulk_uploads/template');

function* loadDataSaga({ payload }: LoadDataAction) {
  try {
    const { page = 1, pageSize = 10, column, order, filter } = payload || {};

    const result: IResponse<ILoadDataSuccessPayload> = yield call(
      sendRequest,
      bulkUploadsApi.load,
      { search: { ...payload, page, pageSize, column, order, filter } },
      notifications.REQUEST_ERROR,
    );

    if (result?.data?.success?.data) {
      const { records, totalCount, filteredCount, filters, sortableColumns } = result.data.success.data;

      yield put(
        actions.loadDataSuccess({
          records,
          totalCount,
          filteredCount,
          filters,
          sortableColumns,
        }),
      );
    }
  } catch (error) {
    logger.error(error);
    yield put(actions.loadDataError());
    yield put(
      notifications.addNotification({
        headline: t('dash.common.notification.error_headline'),
        type: 'error',
        message: t('dash.common.notification.something_went_wrong'),
      }),
    );
  }
}

function* loadFormDataSaga() {
  try {
    const result: IResponse<IBulkUploadsFormDataApiResponse> = yield call(
      sendRequest,
      bulkUploadsApi.loadFormData,
      {},
      notifications.REQUEST_ERROR,
    );

    if (result?.data?.success?.data) {
      const { upload_types: uploadTypes, max_file_size_bytes: maxFileSizeBytes } = result.data.success.data;
      yield put(
        actions.loadFormDataSuccess({
          uploadTypes: uploadTypes || [],
          maxFileSizeBytes: maxFileSizeBytes || DOCUMENT_MAX_SIZE.value,
        }),
      );
    }
  } catch (error) {
    logger.error(error);
    yield put(actions.loadFormDataError());
  }
}

function* uploadFileSaga({ payload }: UploadFileAction) {
  try {
    const { uploadType, file } = payload;

    const formData = new FormData();
    formData.append('bulk_upload[upload_type]', uploadType);
    formData.append('bulk_upload[source_file]', file);

    const result: IResponse<void> = yield call(
      sendRequest,
      bulkUploadsApi.sendFormData,
      { data: formData },
      notifications.REQUEST_ERROR,
    );

    if (!result?.status || result.status >= 300) {
      yield put(actions.uploadFileError());

      return;
    }

    yield put(actions.uploadFileSuccess());
    yield put(actions.clearModal());

    yield put(
      notifications.addNotification({
        headline: t('dash.common.notification.success_headline'),
        type: 'success',
        message: t('dash.data_uploads.notifications.upload_started'),
      }),
    );

    const currentSearch = window.location.search;
    const currentParams = parseQuery(currentSearch);
    yield put(actions.loadData(currentParams));
  } catch (error) {
    logger.error(error);
    yield put(actions.uploadFileError());
  }
}

function* downloadTemplateSaga({ payload }: DownloadTemplateAction) {
  try {
    const result: IResponse<IBulkUploadsTemplateApiResponse> = yield call(
      sendRequest,
      bulkUploadsTemplateApi.load,
      { search: { upload_type: payload } },
      notifications.REQUEST_ERROR,
    );

    if (result?.data) {
      const { fileBlobUrl, fileName } = result.data;
      const link = document.createElement('a');
      link.href = fileBlobUrl;
      link.target = '_blank';
      link.download = fileName || `${payload}_template.csv`;
      link.click();
    }
  } catch (error) {
    logger.error(error);
    yield put(
      notifications.addNotification({
        headline: t('dash.common.notification.error_headline'),
        type: 'error',
        message: t('dash.common.notification.something_went_wrong'),
      }),
    );
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actionTypes.LOAD_DATA_REQUEST, loadDataSaga),
    takeEvery(actionTypes.LOAD_FORM_DATA_REQUEST, loadFormDataSaga),
    takeEvery(actionTypes.UPLOAD_FILE_REQUEST, uploadFileSaga),
    takeEvery(actionTypes.DOWNLOAD_TEMPLATE_REQUEST, downloadTemplateSaga),
  ]);
}
